export type PortalRole = "couple" | "superadmin";

export type PortalSession = {
  isAuthenticated: true;
  role: PortalRole;
  username: string;
  loginAt: string;
};

export type UploadLogItem = {
  id: string;
  filename: string;
  type: "image" | "video";
  category: "photos" | "framed" | "videos";
  status: "uploaded" | "failed" | "pending";
  uploadedAt: string;
  progress: number;
  source: "gallery-upload" | "framed-camera" | "booth";
};

export const COUPLE_USERNAME = "Mr&Mrsstorer";
export const COUPLE_PASSWORD = "wedding2026";

export const SUPERADMIN_USERNAME = "AdminJosh";
export const SUPERADMIN_PASSWORD = "Lottie2026";

const SESSION_KEY = "weddingPortalSession";
const UPLOAD_LOGS_KEY = "weddingUploadLogs";
const KIOSK_SETTINGS_KEY = "weddingKioskSettings";
const PHOTO_BOOTH_KIOSK_ACTIVE_KEY = "weddingPhotoBoothKioskActive";

export const PHOTO_BOOTH_KIOSK_CHANGE_EVENT = "weddingPhotoBoothKioskChange";

export function createSession(role: PortalRole, username: string): PortalSession {
  return {
    isAuthenticated: true,
    role,
    username,
    loginAt: new Date().toISOString(),
  };
}

export function saveSession(session: PortalSession) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getSession(): PortalSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PortalSession;
  } catch {
    return null;
  }
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

export function setPhotoBoothKioskActive(active: boolean) {
  try {
    if (active) {
      localStorage.setItem(PHOTO_BOOTH_KIOSK_ACTIVE_KEY, "1");
    } else {
      localStorage.removeItem(PHOTO_BOOTH_KIOSK_ACTIVE_KEY);
    }

    window.dispatchEvent(new Event(PHOTO_BOOTH_KIOSK_CHANGE_EVENT));
  } catch {}
}

export function isPhotoBoothKioskActive() {
  try {
    return localStorage.getItem(PHOTO_BOOTH_KIOSK_ACTIVE_KEY) === "1";
  } catch {
    return false;
  }
}

export function seedUploadLogs() {
  try {
    const existing = localStorage.getItem(UPLOAD_LOGS_KEY);
    if (existing) return;

    const now = Date.now();
    const sample: UploadLogItem[] = [
      {
        id: "log-1",
        filename: "frame-img-001.png",
        type: "image",
        category: "framed",
        status: "uploaded",
        uploadedAt: new Date(now - 1000 * 60 * 4).toISOString(),
        progress: 100,
        source: "framed-camera",
      },
      {
        id: "log-2",
        filename: "img-002.jpg",
        type: "image",
        category: "photos",
        status: "uploaded",
        uploadedAt: new Date(now - 1000 * 60 * 12).toISOString(),
        progress: 100,
        source: "gallery-upload",
      },
      {
        id: "log-3",
        filename: "video-001.mp4",
        type: "video",
        category: "videos",
        status: "uploaded",
        uploadedAt: new Date(now - 1000 * 60 * 22).toISOString(),
        progress: 100,
        source: "gallery-upload",
      },
      {
        id: "log-4",
        filename: "frame-img-003.png",
        type: "image",
        category: "framed",
        status: "pending",
        uploadedAt: new Date(now - 1000 * 30).toISOString(),
        progress: 72,
        source: "booth",
      },
    ];

    localStorage.setItem(UPLOAD_LOGS_KEY, JSON.stringify(sample));
  } catch {}
}

export function getUploadLogs(): UploadLogItem[] {
  try {
    const raw = localStorage.getItem(UPLOAD_LOGS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as UploadLogItem[];
  } catch {
    return [];
  }
}

export function getUploadSummary() {
  const logs = getUploadLogs();

  return {
    total: logs.length,
    uploaded: logs.filter((x) => x.status === "uploaded").length,
    failed: logs.filter((x) => x.status === "failed").length,
    pending: logs.filter((x) => x.status === "pending").length,
    photos: logs.filter((x) => x.category === "photos").length,
    framed: logs.filter((x) => x.category === "framed").length,
    videos: logs.filter((x) => x.category === "videos").length,
    lastUpload: logs.length
      ? [...logs].sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt))[0].uploadedAt
      : null,
  };
}

export type KioskSettings = {
  enabled: boolean;
  idleSlideshowMinutes: number;
  boothPassword: string;
  updatedAt: string;
};

export function seedKioskSettings() {
  try {
    const existing = localStorage.getItem(KIOSK_SETTINGS_KEY);
    if (existing) return;

    const settings: KioskSettings = {
      enabled: true,
      idleSlideshowMinutes: 5,
      boothPassword: SUPERADMIN_PASSWORD,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(KIOSK_SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
}

export function getKioskSettings(): KioskSettings | null {
  try {
    const raw = localStorage.getItem(KIOSK_SETTINGS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as KioskSettings;
  } catch {
    return null;
  }
}

export function saveKioskSettings(settings: KioskSettings) {
  localStorage.setItem(KIOSK_SETTINGS_KEY, JSON.stringify(settings));
}

export type GuestbookEntry = {
  id: string;
  message: string;
  typedName: string;
  signatureDataUrl: string | null;
  createdAt: string;
  status?: "visible" | "hidden";
};
